import { useState, useEffect, useCallback } from 'react';
import { locationsService } from '../services/locations.service';
import type { Location, LocationFilters, LocationStats, CreateLocationData, UpdateLocationData } from '../types';

export const useLocations = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [stats, setStats] = useState<LocationStats | null>(null);
  const [filters, setFilters] = useState<LocationFilters>({
    estado: 'TODOS',
    tipoLocal: 'TODOS',
    searchQuery: '',
    sortBy: 'nombre',
    sortOrder: 'asc'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar sucursales
  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [locationsData, statsData] = await Promise.all([
        locationsService.getLocations(),
        locationsService.getLocationStats(),
      ]);
      setLocations(locationsData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar sucursales');
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplicar filtros y ordenamiento
  useEffect(() => {
    let filtered = [...locations];

    // Filtro por estado
    if (filters.estado && filters.estado !== 'TODOS') {
      filtered = filtered.filter(location => location.estado === filters.estado);
    }

    // Filtro por tipo de local
    if (filters.tipoLocal && filters.tipoLocal !== 'TODOS') {
      filtered = filtered.filter(location => location.tipoLocal === filters.tipoLocal);
    }

    // Filtro por búsqueda
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(location =>
        location.nombre.toLowerCase().includes(query) ||
        location.direccion.toLowerCase().includes(query) ||
        location.gerente.toLowerCase().includes(query) ||
        location.email.toLowerCase().includes(query)
      );
    }

    // Ordenamiento
    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (filters.sortBy) {
        case 'nombre':
          aValue = a.nombre;
          bValue = b.nombre;
          break;
        case 'estado':
          aValue = a.estado;
          bValue = b.estado;
          break;
        case 'tipo':
          aValue = a.tipoLocal;
          bValue = b.tipoLocal;
          break;
        case 'fechaApertura':
          aValue = new Date(a.fechaApertura).getTime();
          bValue = new Date(b.fechaApertura).getTime();
          break;
        default:
          aValue = a.nombre;
          bValue = b.nombre;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return (filters.sortOrder || 'asc') === 'desc' ? -comparison : comparison;
      } else {
        const comparison = (aValue as number) - (bValue as number);
        return (filters.sortOrder || 'asc') === 'desc' ? -comparison : comparison;
      }
    });

    setFilteredLocations(filtered);
  }, [locations, filters]);

  // Crear sucursal
  const createLocation = async (locationData: CreateLocationData): Promise<Location> => {
    try {
      const newLocation = await locationsService.createLocation(locationData);
      setLocations(prev => [...prev, newLocation]);
      await loadLocations(); // Recargar para actualizar estadísticas
      return newLocation;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear sucursal';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Actualizar sucursal
  const updateLocation = async (locationData: UpdateLocationData): Promise<Location> => {
    try {
      const updatedLocation = await locationsService.updateLocation(locationData);
      setLocations(prev =>
        prev.map(location =>
          location.id === updatedLocation.id ? updatedLocation : location
        )
      );
      await loadLocations(); // Recargar para actualizar estadísticas
      return updatedLocation;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al actualizar sucursal';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Eliminar sucursal
  const deleteLocation = async (id: string): Promise<void> => {
    try {
      await locationsService.deleteLocation(id);
      setLocations(prev => prev.filter(location => location.id !== id));
      await loadLocations(); // Recargar para actualizar estadísticas
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al eliminar sucursal';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Cambiar estado de sucursal
  const toggleLocationStatus = async (id: string, newStatus: Location['estado']): Promise<void> => {
    try {
      const updatedLocation = await locationsService.toggleLocationStatus(id, newStatus);
      setLocations(prev =>
        prev.map(location =>
          location.id === id ? updatedLocation : location
        )
      );
      await loadLocations(); // Recargar para actualizar estadísticas
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cambiar estado';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Obtener sucursal por ID
  const getLocationById = (id: string): Location | undefined => {
    return locations.find(location => location.id === id);
  };

  // Actualizar filtros
  const updateFilters = (newFilters: Partial<LocationFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Limpiar error
  const clearError = () => {
    setError(null);
  };

  // Cargar datos al montar el componente
  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  return {
    // Estado
    locations: filteredLocations,
    allLocations: locations,
    stats,
    filters,
    loading,
    error,

    // Acciones
    loadLocations,
    createLocation,
    updateLocation,
    deleteLocation,
    toggleLocationStatus,
    getLocationById,
    updateFilters,
    clearError,
  };
};